import { useEffect, useRef, useState } from 'react';
import { useQuery, useSubscription, useApolloClient } from '@apollo/client';
import { Announcement } from '@servicetitan/design-system';
import { useNavigate } from 'react-router-dom';
import { isCallContinuityActiveQuery } from './queries/isCallContinuityActive';
import { callContinuityChangedSubscription } from './subscriptions/callContinuityChanged';
import { getAllCallContinuitySettings } from '@pages/settings/CallContinuityPage/queries/getAllCallContinuitySettings';
import { permissionSets } from '@app/permissions';
import usePermissions from '@app/permissions/usePermissions';
import paths from '@app/routes/paths';

const CallContinuityBanner = () => {
  const client = useApolloClient();
  const { data, loading, refetch } = useQuery(isCallContinuityActiveQuery);
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  const hasManageAccess = usePermissions(
    permissionSets.admin.CALL_CONTINUITY_EDIT_NETWORK,
    'all',
  );

  // Re-show banner when call continuity transitions from fully disabled → enabled
  const wasActive = useRef<boolean | null>(null);
  useEffect(() => {
    const isActive = data?.isCallContinuityActive ?? false;
    if (wasActive.current === false && isActive) {
      setDismissed(false);
    }
    wasActive.current = isActive;
  }, [data?.isCallContinuityActive]);

  useSubscription(callContinuityChangedSubscription, {
    onData: ({ data: subscriptionData }) => {
      refetch();

      // Update the settings page cache if it's been loaded
      const changed = subscriptionData.data?.callContinuityChanged;
      if (!changed) return;

      const cached = client.readQuery({ query: getAllCallContinuitySettings });
      if (!cached) return;

      client.writeQuery({
        query: getAllCallContinuitySettings,
        data: {
          callContinuitySettingsForAllTenants:
            cached.callContinuitySettingsForAllTenants.map((setting) =>
              setting.tenantId === changed.tenantId
                ? { ...setting, isEnabled: changed.isEnabled }
                : setting,
            ),
        },
      });
    },
  });

  if (loading || !data?.isCallContinuityActive || dismissed) return null;

  return (
    <Announcement
      status='warning'
      title='Call Continuity enabled by an administrator. Calls for affected tenants are bypassing Contact Center Pro workflows and routing directly to call continuity options.'
      actionName={hasManageAccess ? 'Manage Settings' : undefined}
      onActionClick={
        hasManageAccess ? () => navigate(`/${paths.callContinuity}`) : undefined
      }
      onClose={() => setDismissed(true)}
    />
  );
};

export default CallContinuityBanner;
